import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Tabs, Card, Row, Col, Tag, Spin, message, Table, Button, Space, Progress, Modal, Input } from 'antd';
import { RightOutlined, EyeOutlined, DownloadOutlined, FileTextOutlined, CheckOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { customerAuthService } from '../services/customerAuth';
import customerApi from '../services/customerApi';
import { Customer } from '../services/customers';
import { Business } from '../services/businesses';
import { Task } from '../services/tasks';
import { Letter } from '../services/letters';
import { Dispatch } from '../services/dispatches';
import { useTableColumnSearch } from '../hooks/useTableColumnSearch';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { ResizableTableHeader } from '../components/ResizableTableHeader';
import { ColumnVisibilityToggle } from '../components/ColumnVisibilityToggle';
import { TimelineTab } from '../components/CustomerForm/TimelineTab';
import '../styles/resizable-table.css';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

export const CustomerPersonal: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [activeTab, setActiveTab] = useState<string>('business');
  const [businessSearchText, setBusinessSearchText] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchData();
  }, []);

  // Handle URL hash to switch tabs
  useEffect(() => {
    if (location.hash) {
      const tabKey = location.hash.substring(1); // Remove the # symbol
      if (['business', 'financial', 'task', 'letters', 'dispatch', 'files', 'timeline'].includes(tabKey)) {
        setActiveTab(tabKey);
      }
    }
  }, [location.hash]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [customerResponse, businessesResponse] = await Promise.all([
        customerApi.get('/customer/profile'),
        customerApi.get('/customer/businesses'),
      ]);

      if (customerResponse.data.success) {
        setCustomer(customerResponse.data.data);
      }

      if (businessesResponse.data.success) {
        setBusinesses(businessesResponse.data.data || []);
      }
    } catch (error: any) {
      console.error('Error fetching data:', error);
      message.error(error.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getTypeColor = (type?: string) => {
    switch (type) {
      case 'potential':
        return 'orange';
      case 'active':
        return 'green';
      case 'inactive':
        return 'red';
      default:
        return 'default';
    }
  };

  const getTypeLabel = (type?: string) => {
    switch (type) {
      case 'potential':
        return 'Potential';
      case 'active':
        return 'Active';
      case 'inactive':
        return 'Inactive';
      default:
        return 'Unknown';
    }
  };

  const getEntityTypeColor = (entity?: string) => {
    switch (entity?.toLowerCase()) {
      case 'sole proprietorship':
        return 'red';
      case 'partnership':
        return 'green';
      case 'sdn bhd':
      case 'private limited company':
        return 'red';
      case 'llp':
      case 'limited liability partnership':
        return 'green';
      default:
        return 'default';
    }
  };

  const getEntityTypeLabel = (entity?: string) => {
    switch (entity?.toLowerCase()) {
      case 'sole proprietorship':
        return 'Sole Proprietorship';
      case 'partnership':
        return 'Partnership';
      case 'sdn bhd':
      case 'private limited company':
        return 'Private Limited Company (Sdn. Bhd.)';
      case 'llp':
      case 'limited liability partnership':
        return 'Limited Liability Partnership';
      default:
        return entity || 'Unknown';
    }
  };

  // Group businesses by entity type
  const businessesByEntity = businesses.reduce((acc, business) => {
    const entity = business.entity || 'Unknown';
    if (!acc[entity]) {
      acc[entity] = [];
    }
    acc[entity].push(business);
    return acc;
  }, {} as Record<string, Business[]>);

  // Get the 4 main entity types
  const entityTypes = [
    'Sole Proprietorship',
    'Partnership',
    'Sdn Bhd',
    'LLP',
  ];

  // Map entity types to their variations
  const entityTypeMap: Record<string, string[]> = {
    'Sole Proprietorship': ['sole proprietorship'],
    'Partnership': ['partnership'],
    'Sdn Bhd': ['sdn bhd', 'private limited company'],
    'LLP': ['llp', 'limited liability partnership'],
  };

  const getBusinessesForEntity = (entityType: string) => {
    const variations = entityTypeMap[entityType] || [];
    const allBusinesses: Business[] = [];
    
    variations.forEach(variation => {
      Object.keys(businessesByEntity).forEach(key => {
        if (key.toLowerCase() === variation.toLowerCase()) {
          allBusinesses.push(...businessesByEntity[key]);
        }
      });
    });
    
    return allBusinesses;
  };

  // Filter businesses based on search text
  const filteredBusinessesForDisplay = useMemo(() => {
    if (!businessSearchText.trim()) {
      return businesses;
    }
    
    const searchLower = businessSearchText.toLowerCase();
    return businesses.filter(business => {
      const name = (business.name || '').toLowerCase();
      const registrationNo = (business.registration_no || '').toLowerCase();
      const entity = (business.entity || '').toLowerCase();
      
      return name.includes(searchLower) || 
             registrationNo.includes(searchLower) || 
             entity.includes(searchLower);
    });
  }, [businesses, businessSearchText]);

  // Recalculate businessesByEntity with filtered businesses
  const filteredBusinessesByEntity = useMemo(() => {
    return filteredBusinessesForDisplay.reduce((acc, business) => {
      const entity = business.entity || 'Unknown';
      if (!acc[entity]) {
        acc[entity] = [];
      }
      acc[entity].push(business);
      return acc;
    }, {} as Record<string, Business[]>);
  }, [filteredBusinessesForDisplay]);

  const getFilteredBusinessesForEntity = (entityType: string) => {
    const variations = entityTypeMap[entityType] || [];
    const allBusinesses: Business[] = [];
    
    variations.forEach(variation => {
      Object.keys(filteredBusinessesByEntity).forEach(key => {
        if (key.toLowerCase() === variation.toLowerCase()) {
          allBusinesses.push(...filteredBusinessesByEntity[key]);
        }
      });
    });
    
    return allBusinesses;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!customer) {
    return <div>Customer not found</div>;
  }

  return (
    <div>
      {/* Header Section - Card Style */}
      <Card
        style={{
          marginBottom: 24,
          borderRadius: 8,
          border: '1px solid #d9d9d9',
          padding: 0,
          overflow: 'hidden',
          cursor: 'pointer',
        }}
        bodyStyle={{ padding: 0 }}
        onClick={() => navigate('/customer/personal/detail')}
      >
        {/* Top Section with Blue Background */}
        <div
          style={{
            backgroundColor: '#e6f7ff',
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Title level={4} style={{ margin: 0, color: '#1890ff', fontWeight: 'bold' }}>
            {customer.name}
          </Title>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {customer.type && (
              <Tag
                color={getTypeColor(customer.type)}
                style={{
                  borderRadius: 16,
                  padding: '4px 12px',
                  fontSize: '12px',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <span
                  style={{
                    width: 4,
                    height: 4,
                    borderRadius: '50%',
                    backgroundColor: customer.type === 'potential' ? '#fa8c16' : customer.type === 'active' ? '#52c41a' : '#ff4d4f',
                    display: 'inline-block',
                  }}
                />
                {getTypeLabel(customer.type)}
              </Tag>
            )}
            <RightOutlined style={{ color: '#1890ff', fontSize: 14 }} />
          </div>
        </div>
        
        {/* Divider Line */}
        <div
          style={{
            height: 1,
            backgroundColor: '#e6f7ff',
            width: '100%',
          }}
        />
        
        {/* Bottom Section with White Background */}
        <div
          style={{
            backgroundColor: '#fff',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '8px 12px', width: 180, backgroundColor: '#fafafa' }}>
                  <strong>Identification No.</strong>
                </td>
                <td style={{ padding: '8px 12px', color: '#1890ff' }}>
                  {customer.ic_passport_no || '-'}
                </td>
              </tr>
              <tr>
                <td style={{ padding: '8px 12px', width: 180, backgroundColor: '#fafafa' }}>
                  <strong>Tin No.</strong>
                </td>
                <td style={{ padding: '8px 12px', color: '#1890ff' }}>
                  {customer.tin_no || '-'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {/* Tabs Section */}
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Business" key="business">
          <div style={{ marginTop: 16 }}>
            <div style={{ marginBottom: 16 }}>
              <Input
                placeholder="Search Entity"
                prefix={<SearchOutlined />}
                value={businessSearchText}
                onChange={(e) => setBusinessSearchText(e.target.value)}
                allowClear
                style={{ maxWidth: 300 }}
              />
            </div>
            <Row gutter={[16, 16]}>
              {entityTypes.map((entityType, index) => {
                const entityBusinesses = getFilteredBusinessesForEntity(entityType);
                if (entityBusinesses.length === 0) {
                  return null;
                }
                
                // Get the first business for this entity type (or combine data)
                const business = entityBusinesses[0];
                const status = business.type || 'existing';
                const isActive = status === 'active' || status === 'existing';
                const headerBgColor = isActive ? '#f6ffed' : '#fff1f0';
                const titleColor = isActive ? '#52c41a' : '#ff4d4f';
                const tagBgColor = isActive ? '#f6ffed' : '#fff1f0';
                const tagBorderColor = isActive ? '#b7eb8f' : '#ffccc7';
                const tagTextColor = isActive ? '#52c41a' : '#ff4d4f';
                const dotColor = isActive ? '#52c41a' : '#ff4d4f';
                
                return (
                  <Col key={entityType} xs={24} sm={12} md={12} lg={12}>
                    <Card
                      style={{
                        height: '100%',
                        borderRadius: 8,
                        border: '1px solid #d9d9d9',
                        padding: 0,
                        overflow: 'hidden',
                        cursor: 'pointer',
                      }}
                      bodyStyle={{ padding: 0 }}
                      onClick={() => navigate(`/customer/businesses/${business.id}`)}
                    >
                      {/* Header Section with Colored Background */}
                      <div
                        style={{
                          backgroundColor: headerBgColor,
                          padding: '12px 16px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Title level={5} style={{ margin: 0, color: titleColor, fontWeight: 'bold' }}>
                          {business.name || '-'}
                        </Title>
                        <Tag
                          style={{
                            borderRadius: 16,
                            padding: '4px 12px',
                            fontSize: '12px',
                            backgroundColor: tagBgColor,
                            border: `1px solid ${tagBorderColor}`,
                            color: tagTextColor,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            margin: 0,
                          }}
                        >
                          <span
                            style={{
                              width: 4,
                              height: 4,
                              borderRadius: '50%',
                              backgroundColor: dotColor,
                              display: 'inline-block',
                            }}
                          />
                          {getTypeLabel(status)}
                        </Tag>
                      </div>
                      
                      {/* Content Section with White Background */}
                      <div
                        style={{
                          backgroundColor: '#fff',
                        }}
                      >
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <tbody>
                            <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <td style={{ padding: '8px 12px', width: 160, backgroundColor: '#fafafa' }}>
                                <strong>Registration No.</strong>
                              </td>
                              <td style={{ padding: '8px 12px' }}>{business.registration_no || '-'}</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <td style={{ padding: '8px 12px', width: 160, backgroundColor: '#fafafa' }}>
                                <strong>Entity</strong>
                              </td>
                              <td style={{ padding: '8px 12px' }}>{business.entity || '-'}</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <td style={{ padding: '8px 12px', width: 160, backgroundColor: '#fafafa' }}>
                                <strong>Position</strong>
                              </td>
                              <td style={{ padding: '8px 12px' }}>
                                {business.directors && business.directors.length > 0 && business.directors[0].position
                                  ? business.directors[0].position
                                  : '-'}
                              </td>
                            </tr>
                            <tr>
                              <td style={{ padding: '8px 12px', width: 160, backgroundColor: '#fafafa' }}>
                                <strong>Share</strong>
                              </td>
                              <td style={{ padding: '8px 12px' }}>
                                {business.members && business.members.length > 0 && business.members[0].share
                                  ? `${business.members[0].share}%`
                                  : '-'}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>
            
            {filteredBusinessesForDisplay.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                {businessSearchText ? 'No businesses found matching your search' : 'No businesses found'}
              </div>
            )}
          </div>
        </TabPane>
        
        <TabPane tab="Financial" key="financial">
          <CustomerFinancialList />
        </TabPane>
        
        <TabPane tab="Task" key="task">
          <CustomerTasksList navigate={navigate} />
        </TabPane>
        
        <TabPane tab="Letters" key="letters">
          <CustomerLettersList navigate={navigate} />
        </TabPane>
        
        <TabPane tab="Dispatch" key="dispatch">
          <CustomerDispatchesList navigate={navigate} />
        </TabPane>

        <TabPane tab="Timeline" key="timeline">
          <TimelineTab changeRequests={customer.change_requests || []} />
        </TabPane>
        
      </Tabs>
    </div>
  );
};

// Customer Tasks List Component
const CustomerTasksList: React.FC<{
  navigate: (path: string) => void;
}> = ({ navigate }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [reviewText, setReviewText] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await customerApi.get('/customer/tasks');
        setTasks(response.data.data || []);
      } catch (error: any) {
        message.error(error.response?.data?.message || 'Failed to load tasks');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatTaskNumber = (id: number) => {
    return `#${id.toString().padStart(7, '0')}`;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const filteredTasks = useMemo(() => {
    if (!searchText.trim()) {
      return tasks;
    }
    const query = searchText.toLowerCase();
    return tasks.filter((task) => {
      const fields = [
        task.customer?.name,
        task.taskType?.name,
        task.taskTemplate?.name,
        task.year ? String(task.year) : undefined,
        task.id ? String(task.id) : undefined,
      ];
      return fields.some((field) => field?.toLowerCase().includes(query));
    });
  }, [tasks, searchText]);

  const getChecklistProgress = (task: Task) => {
    const total = task.subTasks?.length ?? 0;
    if (total === 0) return 0;
    const completed = task.subTasks?.filter((item) => item.completed).length ?? 0;
    return Math.round((completed / total) * 100);
  };

  const getStatusInfo = (status?: string) => {
    switch (status) {
      case 'Plan':
        return { 
          color: '#1890ff', 
          progress: 0, 
          label: 'Plan',
          bgColor: '#e6f7ff',
          textColor: '#1890ff',
          tagBgColor: '#e6f7ff',
          tagBorderColor: '#91d5ff',
          tagTextColor: '#1890ff',
          dotColor: '#1890ff'
        };
      case 'Progress':
        return { 
          color: '#fa8c16', 
          progress: 40, 
          label: 'Progress',
          bgColor: '#fff7e6',
          textColor: '#fa8c16',
          tagBgColor: '#fff7e6',
          tagBorderColor: '#ffd591',
          tagTextColor: '#fa8c16',
          dotColor: '#fa8c16'
        };
      case 'Review':
        return { 
          color: '#ff4d4f', 
          progress: 70, 
          label: 'Review',
          bgColor: '#fff1f0',
          textColor: '#ff4d4f',
          tagBgColor: '#fff1f0',
          tagBorderColor: '#ffccc7',
          tagTextColor: '#ff4d4f',
          dotColor: '#ff4d4f'
        };
      case 'Approve':
        return { 
          color: '#13c2c2', 
          progress: 90, 
          label: 'Approve',
          bgColor: '#e6fffb',
          textColor: '#13c2c2',
          tagBgColor: '#e6fffb',
          tagBorderColor: '#87e8de',
          tagTextColor: '#13c2c2',
          dotColor: '#13c2c2'
        };
      case 'Completed':
        return { 
          color: '#52c41a', 
          progress: 100, 
          label: 'Completed',
          bgColor: '#f6ffed',
          textColor: '#52c41a',
          tagBgColor: '#f6ffed',
          tagBorderColor: '#b7eb8f',
          tagTextColor: '#52c41a',
          dotColor: '#52c41a'
        };
      default:
        return { 
          color: '#1890ff', 
          progress: 0, 
          label: 'Plan',
          bgColor: '#e6f7ff',
          textColor: '#1890ff',
          tagBgColor: '#e6f7ff',
          tagBorderColor: '#91d5ff',
          tagTextColor: '#1890ff',
          dotColor: '#1890ff'
        };
    }
  };

  const handleApprove = async (taskId: number) => {
    try {
      await customerApi.post('/customer/task-reviews', {
        task_id: taskId,
        review: 'Approved by customer',
      });
      message.success('Task approved successfully');
      // Reload tasks
      const response = await customerApi.get('/customer/tasks');
      setTasks(response.data.data || []);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to approve task');
    }
  };

  const handleReviewClick = (taskId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedTaskId(taskId);
    setReviewModalVisible(true);
  };

  const handleReviewSubmit = async () => {
    if (!selectedTaskId || !reviewText.trim()) {
      message.error('Please enter a review');
      return;
    }

    setSubmittingReview(true);
    try {
      const customer = customerAuthService.getCurrentCustomer();
      await customerApi.post('/customer/task-reviews', {
        task_id: selectedTaskId,
        customer_id: customer?.id,
        review: reviewText,
      });
      
      message.success('Review submitted successfully');
      setReviewModalVisible(false);
      setReviewText('');
      setSelectedTaskId(null);
      // Reload tasks
      const response = await customerApi.get('/customer/tasks');
      setTasks(response.data.data || []);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: '40px' }} />;
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search task"
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
          style={{ maxWidth: 300 }}
        />
      </div>
      <Row gutter={[16, 16]}>
        {filteredTasks.map((task) => {
          const statusInfo = getStatusInfo(task.status);
          const checklistPercent = getChecklistProgress(task);
          const draftReports = task.draftReports || [];
          const sortedDrafts = [...draftReports].sort((a, b) => {
            const left = a.created_at ? new Date(a.created_at).getTime() : 0;
            const right = b.created_at ? new Date(b.created_at).getTime() : 0;
            return left - right;
          });
          const lastDraft = sortedDrafts[sortedDrafts.length - 1];
          
          return (
            <Col key={task.id} xs={24} sm={12} md={12} lg={12}>
              <Card
                style={{
                  height: '100%',
                  borderRadius: 8,
                  border: '1px solid #d9d9d9',
                  padding: 0,
                  overflow: 'hidden',
                  cursor: 'pointer',
                }}
                bodyStyle={{ padding: 0 }}
                onClick={() => navigate(`/customer/tasks/${task.id}`)}
              >
                {/* Header Section with Colored Background */}
                <div
                  style={{
                    backgroundColor: statusInfo.bgColor,
                    padding: '12px 16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Title level={5} style={{ margin: 0, color: statusInfo.textColor, fontWeight: 'bold' }}>
                    {formatTaskNumber(task.id)}
                  </Title>
                  <Tag
                    style={{
                      borderRadius: 16,
                      padding: '4px 12px',
                      fontSize: '12px',
                      backgroundColor: statusInfo.tagBgColor,
                      border: `1px solid ${statusInfo.tagBorderColor}`,
                      color: statusInfo.tagTextColor,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      margin: 0,
                    }}
                  >
                    <span
                      style={{
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        backgroundColor: statusInfo.dotColor,
                        display: 'inline-block',
                      }}
                    />
                    {statusInfo.label}
                  </Tag>
                  </div>
                
                {/* Content Section with White Background */}
                <div
                  style={{
                    backgroundColor: '#fff',
                    paddingRight: '16px',
                    display: 'flex',
                    gap: 16,
                    position: 'relative',
                    minHeight: 200,
                  }}
              >
                  <div style={{ flex: 1 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                            <strong>Customer</strong>
                          </td>
                          <td colSpan={3} style={{ padding: '8px 12px' }}>{task.customer?.name || '-'}</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                            <strong>Type</strong>
                          </td>
                          <td style={{ padding: '8px 12px' }}>{task.taskType?.name || '-'}</td>
                          <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                            <strong>Year</strong>
                          </td>
                          <td style={{ padding: '8px 12px' }}>{task.year || '-'}</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                            <strong>Task</strong>
                          </td>
                          <td colSpan={3} style={{ padding: '8px 12px' }}>{task.taskTemplate?.name || '-'}</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                            <strong>Create</strong>
                          </td>
                          <td style={{ padding: '8px 12px' }}>{formatDate(task.created_date)}</td>
                          <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                            <strong>Deadline</strong>
                          </td>
                          <td style={{ padding: '8px 12px' }}>{formatDate(task.deadline)}</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                            <strong>Quotation</strong>
                          </td>
                          <td style={{ padding: '8px 12px' }}>
                            {task.quotation?.quotation_number ? (
                              <Button
                                type="link"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (task.quotation?.id) {
                                    navigate(`/quotations/${task.quotation.id}/edit`);
                                  }
                                }}
                                style={{ padding: 0, height: 'auto' }}
                              >
                                {task.quotation.quotation_number}
                              </Button>
                            ) : (
                              <Text>-</Text>
                            )}
                          </td>
                          <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                            <strong>Invoice</strong>
                          </td>
                          <td style={{ padding: '8px 12px' }}>
                            {task.quotation?.invoice?.invoice_number ? (
                              <Button
                                type="link"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (task.quotation?.invoice?.id) {
                                    navigate(`/invoices/${task.quotation.invoice.id}/edit`);
                                  }
                                }}
                                style={{ padding: 0, height: 'auto' }}
                              >
                                {task.quotation.invoice.invoice_number}
                              </Button>
                            ) : (
                              <Text>-</Text>
                            )}
                          </td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                            <strong>Team</strong>
                          </td>
                          <td style={{ padding: '8px 12px' }}>{task.team?.name || '-'}</td>
                          <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                            <strong>Staff</strong>
                          </td>
                          <td style={{ padding: '8px 12px' }}>{task.staff?.name || '-'}</td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                            <strong>Draft</strong>
                          </td>
                          <td colSpan={3} style={{ padding: '8px 12px' }}>
                            {lastDraft?.attachment ? (
                              <Button
                                type="link"
                                icon={<FileTextOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/customer/tasks/${task.id}`);
                                }}
                                style={{ padding: 0, height: 'auto' }}
                              >
                                {lastDraft.attachment.split('/').pop() || 'report.pdf'}
                              </Button>
                            ) : (
                              <Text>-</Text>
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    </div>
                    
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 120 }}>
                    <div style={{ position: 'relative', width: 120, height: 120 }}>
                      <Progress
                        type="circle"
                        percent={checklistPercent}
                        strokeColor={statusInfo.color}
                        format={() => (
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#000' }}>
                              {checklistPercent}%
                            </div>
                            <div style={{ fontSize: '12px', color: statusInfo.color, marginTop: 4 }}>
                              Checklist
                            </div>
                          </div>
                        )}
                        size={120}
                      />
                    </div>
                    </div>

                  {/* Approve Button - Only show for Review status, positioned at bottom right */}
                      {task.status === 'Review' && (
                    <div style={{ position: 'absolute', bottom: 16, right: 16 }}>
                        <Button
                          type="primary"
                          icon={<CheckOutlined />}
                          style={{
                            backgroundColor: '#52c41a',
                            borderColor: '#52c41a',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApprove(task.id);
                          }}
                        >
                          Approve
                        </Button>
                    </div>
                      )}
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>
      {filteredTasks.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          No tasks found
        </div>
      )}

      {/* Review Modal */}
      <Modal
        title="Customer Review"
        open={reviewModalVisible}
        onOk={handleReviewSubmit}
        onCancel={() => {
          setReviewModalVisible(false);
          setReviewText('');
          setSelectedTaskId(null);
        }}
        confirmLoading={submittingReview}
        okText="Submit"
        cancelText="Cancel"
      >
        <Input.TextArea
          rows={6}
          placeholder="Enter your review..."
          value={reviewText}
          onChange={(e) => setReviewText(e.target.value)}
        />
      </Modal>
    </div>
  );
};

// Customer Letters List Component
const CustomerLettersList: React.FC<{
  navigate: (path: string) => void;
}> = ({ navigate }) => {
  const [letters, setLetters] = useState<Letter[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await customerApi.get('/customer/letters');
        setLetters(response.data.data || []);
      } catch (error: any) {
        message.error(error.response?.data?.message || 'Failed to load letters');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatLetterNumber = (letterId: number) => {
    return `#${letterId.toString().padStart(7, '0')}`;
  };

  const formatDate = (date?: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const getLetterStatusInfo = (status?: string) => {
    // Map old status values to new ones
    const mapStatus = (s?: string): 'Plan' | 'Unread' | 'Read' => {
      if (!s) return 'Plan';
      if (s === 'draft') return 'Plan';
      if (s === 'sent') return 'Unread';
      if (['Plan', 'Unread', 'Read'].includes(s)) return s as 'Plan' | 'Unread' | 'Read';
      return 'Plan';
    };
    
    const mappedStatus = mapStatus(status);
    
    if (mappedStatus === 'Unread') {
      return {
        bgColor: '#fff7e6',
        textColor: '#faad14',
        tagBgColor: '#fff7e6',
        tagBorderColor: '#ffe58f',
        tagTextColor: '#faad14',
        dotColor: '#faad14',
        label: 'Unread'
      };
    } else if (mappedStatus === 'Read') {
      return {
        bgColor: '#f6ffed',
        textColor: '#52c41a',
        tagBgColor: '#f6ffed',
        tagBorderColor: '#b7eb8f',
        tagTextColor: '#52c41a',
        dotColor: '#52c41a',
        label: 'Read'
      };
    } else {
      // Default to Plan
      return {
        bgColor: '#e6f7ff',
        textColor: '#1890ff',
        tagBgColor: '#e6f7ff',
        tagBorderColor: '#91d5ff',
        tagTextColor: '#1890ff',
        dotColor: '#1890ff',
        label: 'Plan'
      };
    }
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: '40px' }} />;
  }

  return (
    <div>
      <Row gutter={[16, 16]}>
        {letters.map((letter) => {
          const statusInfo = getLetterStatusInfo(letter.status);
          
          return (
            <Col key={letter.id} xs={24} sm={12} md={12} lg={12}>
              <Card
                style={{
                  height: '100%',
                  borderRadius: 8,
                  border: '1px solid #d9d9d9',
                  padding: 0,
                  overflow: 'hidden',
                  cursor: 'pointer',
                }}
                bodyStyle={{ padding: 0 }}
                onClick={() => navigate(`/customer/letters/${letter.id}`)}
              >
                {/* Header Section with Colored Background */}
                <div
                  style={{
                    backgroundColor: statusInfo.bgColor,
                    padding: '12px 16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Title level={5} style={{ margin: 0, color: statusInfo.textColor, fontWeight: 'bold' }}>
                    {formatLetterNumber(letter.id)}
                  </Title>
                  <Tag
                    style={{
                      borderRadius: 16,
                      padding: '4px 12px',
                      fontSize: '12px',
                      backgroundColor: statusInfo.tagBgColor,
                      border: `1px solid ${statusInfo.tagBorderColor}`,
                      color: statusInfo.tagTextColor,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      margin: 0,
                    }}
                  >
                    <span
                      style={{
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        backgroundColor: statusInfo.dotColor,
                        display: 'inline-block',
                      }}
                    />
                    {statusInfo.label}
                  </Tag>
      </div>
                
                {/* Content Section with White Background */}
                <div
                  style={{
                    backgroundColor: '#fff',
                    position: 'relative',
                    minHeight: 200,
        }}
                >
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                          <strong>Customer</strong>
                        </td>
                        <td style={{ padding: '8px 12px' }}>{letter.customer?.name || '-'}</td>
                        <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                          <strong>Sender</strong>
                        </td>
                        <td style={{ padding: '8px 12px' }}>{letter.sender?.name || '-'}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                          <strong>Subject</strong>
                        </td>
                        <td colSpan={3} style={{ padding: '8px 12px' }}>{letter.subject?.name || '-'}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                          <strong>Create</strong>
                        </td>
                        <td style={{ padding: '8px 12px' }}>{formatDate(letter.create_date)}</td>
                        <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                          <strong>Deadline</strong>
                        </td>
                        <td style={{ padding: '8px 12px' }}>{formatDate(letter.due_date)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                          <strong>Letter</strong>
                        </td>
                        <td colSpan={3} style={{ padding: '8px 12px' }}>
                          {letter.items && letter.items.length > 0 ? (
                            <Button
                              type="link"
                              icon={<FileTextOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/customer/letters/${letter.id}`);
                              }}
                              style={{ padding: 0, height: 'auto' }}
                            >
                              {letter.items[0].attachment?.split('/').pop() || 'letter.pdf'}
                            </Button>
                          ) : (
                            <Text>-</Text>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  
                  {/* Read Button - Only show for sent letters, positioned at bottom right */}
                  {letter.status === 'sent' && (
                    <div style={{ position: 'absolute', bottom: 16, right: 16 }}>
                      <Button
                        type="primary"
                        style={{
                          backgroundColor: '#52c41a',
                          borderColor: '#52c41a',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/customer/letters/${letter.id}/edit`);
                        }}
                      >
                        Read
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>
      {letters.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          No letters found
        </div>
      )}
    </div>
  );
};

// Customer Dispatches List Component
const CustomerDispatchesList: React.FC<{
  navigate: (path: string) => void;
}> = ({ navigate }) => {
  const [dispatches, setDispatches] = useState<Dispatch[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await customerApi.get('/customer/dispatches');
        setDispatches(response.data.data || []);
      } catch (error: any) {
        message.error(error.response?.data?.message || 'Failed to load dispatches');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatDispatchNumber = (dispatchId: number) => {
    return `#${dispatchId.toString().padStart(7, '0')}`;
  };

  const formatDate = (date?: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const getDispatchStatus = (dispatch: Dispatch) => {
    const mapStatus = (status: string | undefined): 'Pending' | 'Collect' | 'Return' => {
      if (!status) return 'Pending';
      const normalized = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
      if (['Pending', 'Collect', 'Return'].includes(normalized)) {
        return normalized as 'Pending' | 'Collect' | 'Return';
      }
      return 'Pending';
    };
    
    const mappedStatus = mapStatus(dispatch.status);
    
    const statusConfig = {
      Pending: { 
        label: 'Pending',
        color: '#1890ff',
        bgColor: '#E6F7FF',
        textColor: '#1890ff',
        tagBgColor: '#E6F7FF',
        tagBorderColor: '#91D5FF',
        tagTextColor: '#1890ff',
        dotColor: '#1890ff'
      },
      Collect: { 
        label: 'Collect',
        color: '#52C41A',
        bgColor: '#F6FFED',
        textColor: '#52C41A',
        tagBgColor: '#F6FFED',
        tagBorderColor: '#73D13D',
        tagTextColor: '#52C41A',
        dotColor: '#52C41A'
      },
      Return: { 
        label: 'Return',
        color: '#FF8C00',
        bgColor: '#FFF7E6',
        textColor: '#FF8C00',
        tagBgColor: '#FFF7E6',
        tagBorderColor: '#FFA940',
        tagTextColor: '#FF8C00',
        dotColor: '#FF8C00'
      },
    };
    
    return statusConfig[mappedStatus];
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: '40px' }} />;
  }

  return (
    <div>
      <Row gutter={[16, 16]}>
        {dispatches.map((dispatch) => {
          const statusInfo = getDispatchStatus(dispatch);
          const taskNumber = dispatch.task?.id || dispatch.task_id;
          const formattedTaskNumber = taskNumber ? `#${String(taskNumber).padStart(7, '0')}` : '-';
          
          return (
            <Col key={dispatch.id} xs={24} sm={12} md={12} lg={12}>
              <Card
                style={{
                  height: '100%',
                  borderRadius: 8,
                  border: '1px solid #d9d9d9',
                  padding: 0,
                  overflow: 'hidden',
                  cursor: 'pointer',
                }}
                bodyStyle={{ padding: 0 }}
                onClick={() => navigate(`/customer/dispatches/${dispatch.id}`)}
              >
                {/* Header Section with Colored Background */}
                <div
                  style={{
                    backgroundColor: statusInfo.bgColor,
                    padding: '12px 16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Title level={5} style={{ margin: 0, color: statusInfo.textColor, fontWeight: 'bold' }}>
                    {formatDispatchNumber(dispatch.id)}
                  </Title>
                  <Tag
                    style={{
                      borderRadius: 16,
                      padding: '4px 12px',
                      fontSize: '12px',
                      backgroundColor: statusInfo.tagBgColor,
                      border: `1px solid ${statusInfo.tagBorderColor}`,
                      color: statusInfo.tagTextColor,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      margin: 0,
                    }}
                  >
                    <span
                      style={{
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        backgroundColor: statusInfo.dotColor,
                        display: 'inline-block',
                      }}
                    />
                    {statusInfo.label}
                  </Tag>
      </div>
                
                {/* Content Section with White Background */}
                <div
                  style={{
                    backgroundColor: '#fff',
                    position: 'relative',
                    minHeight: 200,
        }}
                >
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                          <strong>Customer</strong>
                        </td>
                        <td colSpan={3} style={{ padding: '8px 12px' }}>{dispatch.customer?.name || '-'}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                          <strong>Task No.</strong>
                        </td>
                        <td colSpan={3} style={{ padding: '8px 12px' }}>{formattedTaskNumber}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                          <strong>Type</strong>
                        </td>
                        <td style={{ padding: '8px 12px' }}>{dispatch.taskType?.name || '-'}</td>
                        <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                          <strong>Year</strong>
                        </td>
                        <td style={{ padding: '8px 12px' }}>{dispatch.year || '-'}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                          <strong>Task</strong>
                        </td>
                        <td colSpan={3} style={{ padding: '8px 12px' }}>{dispatch.task?.taskTemplate?.name || '-'}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                          <strong>Collect</strong>
                        </td>
                        <td style={{ padding: '8px 12px' }}>{formatDate(dispatch.collect_date)}</td>
                        <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                          <strong>Collect Due</strong>
                        </td>
                        <td style={{ padding: '8px 12px' }}>{formatDate(dispatch.collect_due)}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                          <strong>Return</strong>
                        </td>
                        <td style={{ padding: '8px 12px' }}>{formatDate(dispatch.return_date)}</td>
                        <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                          <strong>Return Due</strong>
                        </td>
                        <td style={{ padding: '8px 12px' }}>{formatDate(dispatch.return_due)}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                          <strong>Location</strong>
                        </td>
                        <td colSpan={3} style={{ padding: '8px 12px' }}>{dispatch.location || '-'}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                          <strong>Advisor</strong>
                        </td>
                        <td colSpan={3} style={{ padding: '8px 12px' }}>{dispatch.agent?.name || '-'}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                          <strong>Collect</strong>
                        </td>
                        <td style={{ padding: '8px 12px' }}>{formatDate(dispatch.collect_date)}</td>
                        <td style={{ padding: '8px 12px', width: 120, backgroundColor: '#fafafa' }}>
                          <strong>Return</strong>
                        </td>
                        <td style={{ padding: '8px 12px' }}>{formatDate(dispatch.return_date)}</td>
                      </tr>
                    </tbody>
                  </table>
                  
                  {/* Acknowledge Button - Positioned at bottom right */}
                  {['Collect', 'Return'].includes(dispatch.status || '') && (() => {
                    // Check if there's already an acknowledge record for the current status
                    const hasAcknowledgeRecord = dispatch.acknowledge_logs?.some((log: any) => {
                      if (dispatch.status === 'Collect') {
                        return log.to_status === 'Acknowledge (collect)';
                      } else if (dispatch.status === 'Return') {
                        return log.to_status === 'Acknowledge (return)';
                      }
                      return false;
                    });
                    
                    // Only show button if no acknowledge record exists for current status
                    if (!hasAcknowledgeRecord) {
                      return (
                        <div style={{ position: 'absolute', bottom: 16, right: 16 }}>
                          <Button
                            type="primary"
                            style={{
                              backgroundColor: statusInfo.color,
                              borderColor: statusInfo.color,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              // Handle acknowledge action
                              navigate(`/customer/dispatches/${dispatch.id}`);
                            }}
                          >
                            {dispatch.status === 'Collect' ? 'Collect Acknowledge' : 'Return Acknowledge'}
                          </Button>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>
      {dispatches.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          No dispatches found
        </div>
      )}
    </div>
  );
};

// Customer Financial List Component
interface CustomerFinancialReport {
  id: number;
  customer_id: number;
  business_id?: number | null;
  name: string;
  path: string;
  type: string;
  mime_type: string;
  size: number;
  folder: string | null;
  report_type: 'SSM' | 'Financial' | null;
  task: string | null;
  year: number | null;
  description: string | null;
  created_at: string;
  updated_at: string;
}

const CustomerFinancialList: React.FC = () => {
  const [reports, setReports] = useState<CustomerFinancialReport[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await customerApi.get('/customer/reports');
        // Filter only Financial reports with business_id = null (customer reports only)
        const financialReports = (response.data.data || []).filter(
          (report: CustomerFinancialReport) => 
            report.report_type === 'Financial' && 
            (report.business_id === null || report.business_id === undefined)
        );
        setReports(financialReports);
      } catch (error: any) {
        message.error(error.response?.data?.message || 'Failed to load financial reports');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes >= 1073741824) {
      return (bytes / 1073741824).toFixed(2) + ' GB';
    } else if (bytes >= 1048576) {
      return (bytes / 1048576).toFixed(2) + ' MB';
    } else if (bytes >= 1024) {
      return (bytes / 1024).toFixed(2) + ' KB';
    } else {
      return bytes + ' bytes';
    }
  };

  const getFileIcon = (type: string) => {
    const iconStyle = { fontSize: 48, color: '#ff4d4f' };
    return <FileTextOutlined style={iconStyle} />;
  };

  const handleView = async (report: CustomerFinancialReport) => {
    try {
      const response = await customerApi.get(`/customer/reports/${report.id}/view`, {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], { type: response.headers['content-type'] || report.mime_type });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error: any) {
      message.error('Failed to view file');
    }
  };

  const handleDownload = async (report: CustomerFinancialReport) => {
    try {
      const response = await customerApi.get(`/customer/reports/${report.id}/download`, {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], { type: response.headers['content-type'] || report.mime_type });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = report.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error: any) {
      message.error('Failed to download file');
    }
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: '40px' }} />;
  }

  return (
    <div style={{ padding: '24px', background: '#fff' }}>
      <Row gutter={[16, 16]}>
        {reports.map((report) => (
          <Col key={report.id} xs={24} sm={8} md={8} lg={8}>
            <Card
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
              bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '16px' }}
            >
              <div style={{ display: 'flex', gap: 16, flex: 1 }}>
                <div style={{ textAlign: 'center', minWidth: 60 }}>
                  {getFileIcon(report.type)}
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <div>
                    <Text strong style={{ display: 'block', marginBottom: 4, fontSize: '14px' }}>
                      {report.name}
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {report.type?.toUpperCase() || 'FILE'} • {formatFileSize(report.size)}
                    </Text>
                    {report.year && (
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                        Year: {report.year}
                      </Text>
                    )}
                  </div>
                  <Space style={{ marginTop: 12 }}>
                    <Button
                      type="primary"
                      icon={<EyeOutlined />}
                      onClick={() => handleView(report)}
                      size="small"
                    >
                      View
                    </Button>
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={() => handleDownload(report)}
                      size="small"
                    />
                  </Space>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
      {reports.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          No financial reports found
        </div>
      )}
    </div>
  );
};

// Customer Files List Component
interface CustomerFile {
  id: number;
  customer_id: number;
  name: string;
  path: string;
  type: string;
  mime_type: string;
  size: number;
  folder: string | null;
  description: string | null;
  created_at: string;
  updated_at: string;
}

const CustomerFilesList: React.FC = () => {
  const [files, setFiles] = useState<CustomerFile[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await customerApi.get('/customer/files');
        setFiles(response.data.data || []);
      } catch (error: any) {
        message.error(error.response?.data?.message || 'Failed to load files');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes >= 1073741824) {
      return (bytes / 1073741824).toFixed(2) + ' GB';
    } else if (bytes >= 1048576) {
      return (bytes / 1048576).toFixed(2) + ' MB';
    } else if (bytes >= 1024) {
      return (bytes / 1024).toFixed(2) + ' KB';
    } else {
      return bytes + ' bytes';
    }
  };

  const getFileIcon = (type: string) => {
    const iconStyle = { fontSize: 48, color: '#ff4d4f' };
    return <FileTextOutlined style={iconStyle} />;
  };

  const handleView = async (file: CustomerFile) => {
    try {
      const response = await customerApi.get(`/customer/files/${file.id}/view`, {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], { type: response.headers['content-type'] || file.mime_type });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error: any) {
      message.error('Failed to view file');
    }
  };

  const handleDownload = async (file: CustomerFile) => {
    try {
      const response = await customerApi.get(`/customer/files/${file.id}/download`, {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], { type: response.headers['content-type'] || file.mime_type });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error: any) {
      message.error('Failed to download file');
    }
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: '40px' }} />;
  }

  return (
    <div style={{ padding: '24px', background: '#fff' }}>
      <Row gutter={[16, 16]}>
        {files.map((file) => (
          <Col key={file.id} xs={24} sm={8} md={8} lg={8}>
            <Card
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
              bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '16px' }}
            >
              <div style={{ display: 'flex', gap: 16, flex: 1 }}>
                <div style={{ textAlign: 'center', minWidth: 60 }}>
                  {getFileIcon(file.type)}
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <div>
                    <Text strong style={{ display: 'block', marginBottom: 4, fontSize: '14px' }}>
                      {file.name}
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {file.type?.toUpperCase() || 'FILE'} • {formatFileSize(file.size)}
                    </Text>
                  </div>
                  <Space style={{ marginTop: 12 }}>
                    <Button
                      type="primary"
                      icon={<EyeOutlined />}
                      onClick={() => handleView(file)}
                      size="small"
                    >
                      View
                    </Button>
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={() => handleDownload(file)}
                      size="small"
                    />
                  </Space>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
      {files.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          No files found
        </div>
      )}
    </div>
  );
};
